var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, FlexBox, Page, SearchInput, SimplePagination, Table, Text, Timestamp, } from '@dixa/lib-shared-habla';
import upperFirst from 'lodash/upperFirst';
import { useNavigate } from 'react-router-dom';
import { useMount, useSetState } from 'react-use';
import { analytics } from 'app/utils/segment';
import { ConfirmModal } from 'components/shared/confirmModal/components/confirmModal';
import { TableLoadingRow } from 'components/shared/tableLoadingRow';
import { withAutomations, } from 'hocs';
import { AutomationChannelHumanNames } from 'hocs/automations/api/types';
import { useNotifications } from 'hooks/useNotifications';
import { AutomationDotsMenu } from './automationDotsMenu';
import { EditAutomationModal } from './editAutomationModal';
import { ToggleAutomation } from './toggleAutomation';
const COLUMN_COUNT = 5;
export const AutomationsImpl = (props) => {
    const navigate = useNavigate();
    const notify = useNotifications();
    const [state, setState] = useSetState({
        waitingForFetch: false,
        isConfirmModalOpen: false,
        confirmAutomation: null,
        isCreateModalOpen: false,
        editModalID: null,
    });
    useMount(() => {
        props.automations.getItems();
    });
    const handleSearchSubmit = (searchTerm) => {
        const { automations } = props;
        if (searchTerm !== automations.searchTerm) {
            analytics.automationsSearchApplied();
            automations.searchTermChange(searchTerm, 0);
        }
    };
    const handleSearchToggle = (isOpen) => {
        if (isOpen) {
            analytics.automationsSearchOpened();
        }
        else {
            const { automations } = props;
            analytics.automationsSearchClosed();
            if (automations.searchTerm !== '') {
                automations.searchTermChange('', 0);
            }
        }
    };
    const gotoNextPage = () => {
        analytics.automationsPaginationNextClicked();
        props.automations.nextPage();
    };
    const gotoPreviousPage = () => {
        analytics.automationsPaginationPreviousClicked();
        props.automations.previousPage();
    };
    const handleRefresh = () => {
        props.automations.getItems();
    };
    const showCreateModal = () => {
        setState({ isCreateModalOpen: true, editModalID: null });
    };
    const showEditModal = (id) => {
        setState({ isCreateModalOpen: true, editModalID: id });
    };
    const handleCreateModalClose = () => {
        setState({ isCreateModalOpen: false });
    };
    const handleGotoAutomationFlow = (automationId) => {
        navigate(`/settings/automations/flow/${automationId}`);
    };
    const handleGotoAuditLogs = (automationId) => {
        navigate(`/settings/automations/flow/${automationId}/history`);
    };
    const handleDuplicateAutomation = (automationId) => {
        const { automations } = props;
        automations
            .getItem(automationId)
            .then((getResponse) => {
            const _a = getResponse.data, { id, created_at, updated_at } = _a, automation = __rest(_a, ["id", "created_at", "updated_at"]);
            const automationCopyName = `${automation.name} (copy)`;
            const automationCopy = Object.assign(Object.assign({}, automation), { name: automationCopyName, active: false });
            return automations.create(automationCopy).then((createResponse) => {
                notify({
                    title: `Automation copied`,
                    text: `Automation was succesfully copied as "${automationCopyName}"`,
                    className: 'success',
                    time: 5000,
                    icon: 'done',
                });
                handleGotoAutomationFlow(createResponse.data.id);
            });
        })
            .catch(() => {
            notify({
                title: 'An error occurred',
                text: `An error occurred when trying to copy automation`,
                icon: 'exclamation',
                className: 'danger',
            });
        });
    };
    const handleOpenConfirmDeleteAutomation = (automationId) => {
        const { automations } = props;
        const deleteAutomation = automations.items.find((automation) => automation.id === automationId);
        if (deleteAutomation) {
            setState({
                isConfirmModalOpen: true,
                confirmAutomation: deleteAutomation,
            });
        }
    };
    const handleDeleteAutomation = () => {
        const { automations } = props;
        const { confirmAutomation } = state;
        if (confirmAutomation) {
            automations
                .remove(confirmAutomation.id)
                .then(() => {
                handleRefresh();
                closeConfirmDeleteAutomation();
                notify({
                    title: `Automation deleted`,
                    text: `Automation was succesfully deleted`,
                    className: 'success',
                    time: 5000,
                    icon: 'done',
                });
            })
                .catch(() => {
                closeConfirmDeleteAutomation();
                notify({
                    title: 'An error occurred',
                    text: `An error occurred when trying to delete automation`,
                    icon: 'exclamation',
                    className: 'danger',
                });
            });
        }
    };
    const closeConfirmDeleteAutomation = () => {
        setState({
            isConfirmModalOpen: false,
            confirmAutomation: null,
        });
    };
    const renderFoot = () => {
        const { automations } = props;
        const { hasNextPage, hasPreviousPage, getItemsAsyncStatus } = automations;
        const hasFoot = hasNextPage || hasPreviousPage;
        if (!hasFoot) {
            return null;
        }
        return (_jsx(Table.Tfoot, { children: _jsx(Table.Tr, { children: _jsx(Table.Td, Object.assign({ colSpan: COLUMN_COUNT }, { children: _jsx(SimplePagination, { previousButtonClick: gotoPreviousPage, nextButtonClick: gotoNextPage, previousButtonDisabled: !hasPreviousPage, nextButtonDisabled: !hasNextPage, loading: getItemsAsyncStatus === 'loading' }) })) }) }));
    };
    const renderLoadingStatus = () => {
        const { automations } = props;
        if (automations.getItemsAsyncStatus === 'failed') {
            return (_jsx(Table.Tr, { children: _jsxs(Table.Td, Object.assign({ colSpan: COLUMN_COUNT }, { children: ["Something went wrong...", _jsx(Button, Object.assign({ variant: "tertiary", onClick: handleRefresh }, { children: "Try again" }))] })) }));
        }
        return _jsx(TableLoadingRow, { colSpan: COLUMN_COUNT });
    };
    const renderRow = (automation) => {
        return (_jsxs(Table.Tr, { children: [_jsx(Table.Td, { children: _jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "100" }, { children: [_jsx(Text.Paragraph, Object.assign({ variant: "2" }, { children: automation.name })), _jsx(Text.Paragraph, Object.assign({ variant: "4", subdued: true }, { children: automation.description }))] })) }), _jsx(Table.Td, { children: upperFirst(automation.channels
                        .map((name) => AutomationChannelHumanNames[name])
                        .join(', ')) }), _jsx(Table.Td, { children: automation.updated_at ? (_jsx(Timestamp, { relative: true, time: automation.updated_at })) : ('-') }), _jsx(Table.Td, { children: _jsx(ToggleAutomation, { automation: automation, automations: props.automations, disabled: props.automations.updateAsyncStatus !== 'succeeded' }) }), _jsx(Table.Td, { children: _jsx(AutomationDotsMenu, { automationId: automation.id, showSettingsModal: showEditModal, gotoAutomationFlow: handleGotoAutomationFlow, gotoAuditLogs: handleGotoAuditLogs, duplicateAutomation: handleDuplicateAutomation, deleteAutomation: handleOpenConfirmDeleteAutomation }) })] }, automation.id));
    };
    const renderBody = () => {
        const { waitingForFetch } = state;
        const { automations } = props;
        const { items, getItemsAsyncStatus } = automations;
        if (waitingForFetch || getItemsAsyncStatus !== 'succeeded') {
            return renderLoadingStatus();
        }
        if (items.length > 0) {
            return items.map(renderRow);
        }
        return (_jsx(Table.Tr, { children: _jsx(Table.Td, Object.assign({ colSpan: COLUMN_COUNT }, { children: "No automations found." })) }));
    };
    const { automations } = props;
    const { editModalID, isCreateModalOpen, isConfirmModalOpen, confirmAutomation, } = state;
    return (_jsxs(_Fragment, { children: [_jsx(Page.Section, Object.assign({ title: "All automations", controls: _jsxs(_Fragment, { children: [_jsx(SearchInput, { onSubmit: handleSearchSubmit, onToggle: handleSearchToggle }), _jsx(Button, Object.assign({ onClick: showCreateModal, id: "add-automation-btn" }, { children: "Add automation" }))] }) }, { children: _jsxs(Table, Object.assign({ framed: true, stickyHeader: true, stickyFooter: true, headerWithBackground: true }, { children: [_jsx(Table.Thead, { children: _jsxs(Table.Tr, { children: [_jsx(Table.Th, Object.assign({ width: "50%" }, { children: "Name" })), _jsx(Table.Th, Object.assign({ width: "25%" }, { children: "Channel" })), _jsx(Table.Th, Object.assign({ width: "25%" }, { children: "Updated" })), _jsx(Table.Th, { children: "Enable" }), _jsx(Table.Th, {})] }) }), _jsx(Table.Tbody, { children: renderBody() }), renderFoot()] })) })), _jsx(EditAutomationModal, { isOpen: isCreateModalOpen, onClose: handleCreateModalClose, automationId: editModalID, automations: automations, gotoFlow: handleGotoAutomationFlow, refreshPage: handleRefresh }), _jsx(ConfirmModal, { isShown: isConfirmModalOpen, headline: "Are you sure?", text: `Are you sure you want to delete the automation
        "${confirmAutomation === null || confirmAutomation === void 0 ? void 0 : confirmAutomation.name}"?`, confirmText: "Yes, delete it now", cancelText: "Cancel", confirm: handleDeleteAutomation, cancel: closeConfirmDeleteAutomation })] }));
};
export const Automations = withAutomations(AutomationsImpl);
